import { Method } from 'axios';
import {
  DefaultError,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from '@tanstack/react-query';

import { useDialog, useApiContext } from 'src/compass/site/providers';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { onErrorDefaultHandler } from 'src/compass/shared/util/onErrorHandlers';
import { CasLoaderWithBackdrop, CasMutationErrorDialog } from 'src/compass/shared/components';

export interface GenericMutationProps
  extends UseMutationOptions<unknown, DefaultError, any, unknown> {
  errorTitle?: string;
  showLoader?: boolean;
  includesAttachment?: boolean;
  params?: Record<string, any>;
}

export type GenericMutationResult = {
  mutation: UseMutationResult<unknown, DefaultError, Object, unknown>;
};

interface useCasMutationProps extends GenericMutationProps {
  method: Method;
  url: string;
}

export default function useCasMutation({
  method,
  url,
  params,
  includesAttachment=false,
  onSuccess = onSuccessEnqueueSnackbar,
  onError,
  errorTitle,
  showLoader = true,
  ...rest
}: useCasMutationProps) {
  const { api } = useApiContext();
  const dialog = useDialog();

  const mutation = useMutation({
    mutationFn: async (mutationData: Object | void) => {
      const payload = { ...mutationData, ...params };
      try {
        const response = await api({
          method,
          url,
          ...(method.toLowerCase() === 'get' ? { params: payload } : { data: payload }),
          ...(includesAttachment ? {headers: { 'Content-Type': 'multipart/form-data' }} : {})
        });

        return response.data;
      } catch (error) {
        return Promise.reject(onErrorDefaultHandler(error));
      }
    },
    onSuccess: (data, variables, context) => {
      dialog.close();
      if (onSuccess) onSuccess(data, variables, context);
    },
    onError: (error, variables, context) => {
      dialog.open(CasMutationErrorDialog, {
        title: errorTitle,
        mutation: { reset: mutation.reset, error },
      });
      if (onError) onError(error, variables, context);
    },
    ...rest,
  });

  if (mutation.isPending && showLoader) {
    dialog.open(CasLoaderWithBackdrop, {});
  }

  return mutation;
}